import { useRef } from 'react';
import { Button } from 'antd';
import { ArrowRightOutlined, CheckOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './style.scss';

export const NEXT_STEP_PHASE = {
  CHECKING: 'checking',
  // No next step to offer here: the result is slow, failed to load, or is "no next round".
  SUBMITTED: 'submitted',
  READY: 'ready',
  // Cleared for the interview, chose to start it later from the emailed link.
  LATER: 'later',
};

const withSource = (interviewUrl) => {
  try {
    const url = new URL(interviewUrl);
    url.searchParams.set('from', 'assessment');
    return url.toString();
  } catch (error) {
    return interviewUrl;
  }
};

/**
 * Assessment → Result → Next step, drawn as one rail that carries across phases: the result
 * stop pulses while it is checked and turns into a clock once it is with the team. On ready the
 * last segment fills and the interview stop pulses; on later it turns into a clock.
 * @param {string} phase one of NEXT_STEP_PHASE
 */
const ProgressRail = ({ phase }) => {
  const { t } = useTranslation();
  const isReady = phase === NEXT_STEP_PHASE.READY;
  const isLater = phase === NEXT_STEP_PHASE.LATER;

  const resultState = {
    [NEXT_STEP_PHASE.CHECKING]: 'active',
    [NEXT_STEP_PHASE.SUBMITTED]: 'waiting',
    [NEXT_STEP_PHASE.READY]: 'done',
    [NEXT_STEP_PHASE.LATER]: 'done',
  }[phase];

  const stops = [
    { key: 'assessment', state: 'done', label: t('end_screen.step_assessment') },
    { key: 'result', state: resultState, label: t('end_screen.step_result') },
    {
      key: 'next',
      state: isReady ? 'active' : isLater ? 'waiting' : 'upcoming',
      label: isReady || isLater ? t('end_screen.step_interview') : t('end_screen.step_next'),
    },
  ];

  return (
    <div className="progress-rail">
      <span className="progress-rail-link progress-rail-link-1 is-filled" aria-hidden="true">
        <span className="progress-rail-fill" />
      </span>
      <span
        className={`progress-rail-link progress-rail-link-2 ${isReady || isLater ? 'is-filled' : ''}`}
        aria-hidden="true"
      >
        <span className="progress-rail-fill" />
      </span>
      <ol className="progress-rail-stops" aria-label={t('end_screen.progress_label')}>
        {stops.map(({ key, state, label }) => (
          <li
            key={key}
            className={`progress-rail-stop is-${state}`}
            aria-current={['active', 'waiting'].includes(state) ? 'step' : undefined}
          >
            <span className="progress-rail-node" aria-hidden="true">
              {state === 'done' && <CheckOutlined />}
              {state === 'waiting' && <ClockCircleOutlined />}
            </span>
            <span className="progress-rail-label">{label}</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

/**
 * Where the candidate lands once the assessment is in and the AI interview may follow.
 * On ready the candidate either starts the interview, which leaves in the same tab (a full
 * navigation releases every camera/mic stream the assessment held; replace keeps Back off this
 * page), or chooses to start it later from the emailed link.
 * @param {string} phase one of NEXT_STEP_PHASE
 * @param {string} interviewUrl READY only
 * @param {function} onLeave READY only: runs right before navigating (session cleanup)
 * @param {function} onLater READY only: the candidate will start the interview from the email
 */
export const NextStepScreen = ({ phase, interviewUrl, onLeave, onLater }) => {
  const { t } = useTranslation();
  const isReady = phase === NEXT_STEP_PHASE.READY;
  const isLater = phase === NEXT_STEP_PHASE.LATER;
  const leftRef = useRef(false);

  const go = () => {
    if (leftRef.current) return;
    leftRef.current = true;
    onLeave?.();
    window.location.replace(withSource(interviewUrl));
  };

  return (
    <div className={`next-step-screen is-${phase}`}>
      <ProgressRail phase={phase} />
      {/* Stable live region so phase changes are announced; the keyed child replays the
          entrance for each phase while the rail animates between them. */}
      <div aria-live="polite">
        <div key={phase} className="next-step-content">
          {isReady && (
            <>
              <h1 className="next-step-title">{t('end_screen.next_round_ready_title')}</h1>
              <p className="next-step-body">{t('end_screen.next_step_thanks')}</p>
              <p className="next-step-body">{t('end_screen.next_round_ready_body')}</p>
              <div className="next-step-actions">
                <Button onClick={go} className="next-step-cta">
                  {t('end_screen.next_round_start_now')}
                  <ArrowRightOutlined className="next-step-cta-icon" />
                </Button>
                <Button type="text" onClick={onLater} className="next-step-later">
                  {t('end_screen.next_round_later')}
                </Button>
              </div>
            </>
          )}
          {phase === NEXT_STEP_PHASE.CHECKING && (
            <>
              <h1 className="next-step-title">{t('end_screen.next_step_checking_title')}</h1>
              <p className="next-step-body">{t('end_screen.next_step_thanks')}</p>
              <p className="next-step-body">{t('end_screen.next_step_checking_body')}</p>
              <p className="next-step-status">
                <span className="next-step-status-dot" aria-hidden="true" />
                {t('end_screen.next_step_checking_hint')}
              </p>
            </>
          )}
          {phase === NEXT_STEP_PHASE.SUBMITTED && (
            <>
              <h1 className="next-step-title">{t('end_screen.next_step_submitted_title')}</h1>
              <p className="next-step-body">{t('end_screen.next_step_submitted_body')}</p>
              <p className="next-step-body">{t('end_screen.next_step_submitted_detail')}</p>
              <p className="next-step-status">{t('end_screen.next_step_submitted_close')}</p>
            </>
          )}
          {isLater && (
            <>
              <h1 className="next-step-title">{t('end_screen.next_step_later_title')}</h1>
              <p className="next-step-body">{t('end_screen.next_step_later_body')}</p>
              <p className="next-step-status">{t('end_screen.next_step_submitted_close')}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
